import React, { useState } from 'react';
import Joyride, { ACTIONS, EVENTS } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { DICTIONARY_GUIDE } from '@src/constants/tourGuide';
import { CLOSE_DICTIONARY_GUIDE_DESKTOP } from '@src/constants/dictionary';
import Gif from '@src/assets/gifs/reading-method.gif';
import Step from './Step';
import Next from './NextButton';
import Skip from './SkipButton';
import Back from './BackButton';
import Tooltip from './Tooltip';

const MOBILE_BREAKPOINT = 900;

const DictionaryGuide = ({
  run,
  onChange,
  onViewDetail,
  stepDisplay = true,
}) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [isSkip, setIsSkip] = useState(false);

  const stepsGuide = [
    {
      title: 'readingWayFeature',
      steps: [
        {
          name: t('dictionaryGuide'),
          target: `#${DICTIONARY_GUIDE.DICTIONARY_BTN}`,
          showContent: (
            <img
              src={Gif}
              alt="Gif"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                border: '0.62px rgba(60, 60, 67, 0.16) solid',
              }}
            />
          ),
        },
      ],
    },
  ];

  const indexArray = stepsGuide.reduce(
    (acc, curr) => {
      const lengthSteps = curr.steps.length;
      return [...acc, lengthSteps + acc[acc.length - 1]];
    },
    [0],
  );

  const indexLastStep = indexArray.pop() - 1;
  const lengthIndexArray = indexArray.length;

  const handleJoyrideCallback = (data) => {
    const { action, index, type } = data;
    if (isSkip) setIsSkip(false);
    if (
      [EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type) &&
      !isSkip
    ) {
      const activeStep = action === ACTIONS.PREV ? index - 1 : index + 1;
      if (action === ACTIONS.NEXT && index === indexLastStep) {
        onChange(false);
        return;
      }
      setStepIndex(activeStep);
    }
    if (action === ACTIONS.SKIP) onChange(false);
    // handle case user click icon close
    if (type === EVENTS.TOUR_END) {
      localStorage.setItem(CLOSE_DICTIONARY_GUIDE_DESKTOP, true);
    }
  };

  const handleSkipTour = () => {
    setIsSkip(true);
    if (lengthIndexArray < 2) {
      onChange(false);
      return;
    }

    for (let i = 0; i < lengthIndexArray - 1; i += 1) {
      if (stepIndex >= indexArray[i] && stepIndex < indexArray[i + 1]) {
        setStepIndex(indexArray[i + 1]);
        return;
      }
    }
    if (stepIndex >= indexArray[lengthIndexArray - 1]) {
      onChange(false);
    }
  };

  const steps = stepsGuide.reduce((acc, curr, indexStep) => {
    const allSteps = curr.steps?.length ?? 0;
    const lastCluster = indexStep === stepsGuide.length - 1;
    const newSteps = curr.steps.map((item, index) => {
      const lastStep = lastCluster && index === allSteps - 1;
      const firstStep = index === 0 && indexStep === 0;
      return {
        content: item.name ? (
          <>
            <Typography
              sx={{ marginTop: '-8px', marginBottom: '8px' }}
              className="text-content"
            >
              {t(item.name)}
            </Typography>
            <div>{item?.showContent}</div>
          </>
        ) : (
          <div className="text-content">{item.content}</div>
        ),
        title: (
          <Typography className="text-content">{t(curr.title)}</Typography>
        ),
        locale: {
          skip: <Skip handleSkipTour={handleSkipTour} />,
          step: (
            <Step
              steps={allSteps}
              activeStep={index}
              stepDisplay={stepDisplay}
            />
          ),
          back: !firstStep && <Back steps={allSteps} activeStep={index} />,
          next: (
            <Next
              title={lastStep ? t('understand') : t('next')}
              isDone={lastStep}
              onFinish={() => onViewDetail(true)}
            />
          ),
        },
        target: item.target,
        disableBeacon: true,
      };
    });
    const newAcc = [...acc, ...newSteps];
    return newAcc;
  }, []);

  return (
    <Joyride
      callback={handleJoyrideCallback}
      tooltipComponent={(props) => <Tooltip {...props} dictionaryGuide />}
      continuous
      run={window.innerWidth <= MOBILE_BREAKPOINT ? false : run}
      scrollToFirstStep
      disableCloseOnEsc
      disableOverlayClose
      stepIndex={stepIndex}
      steps={steps}
      styles={{
        options: {
          zIndex: 10000,
        },
        spotlight: {
          borderRadius: 12,
          border: '2px solid #FFD60A',
        },
      }}
    />
  );
};

export default DictionaryGuide;
